import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, Shield, Users, Wifi } from "lucide-react";
import logo from "@/assets/apnedoctors-logo.png";


const AI_MESSAGES = [
  "Initializing AI Medical Systems",
  "Preparing Secure Environment",
  "Connecting Doctor Network",
  "Activating Clinical Assistant",
];

const TRUST_ITEMS = [
  { icon: Shield, label: "HIPAA Secure" },
  { icon: Activity, label: "AI Ready" },
  { icon: Wifi, label: "Live Connected" },
  { icon: Users, label: "Doctors Online" },
];

function ParticleField() {
  const particles = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 2 + 1,
        duration: Math.random() * 4 + 6,
        delay: Math.random() * 3,
      })),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-primary/20"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{ y: [0, -30, 0], opacity: [0.1, 0.5, 0.1] }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

function NeuralGrid() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[20, 40, 60, 80].map((y) => (
        <motion.div
          key={`h-${y}`}
          className="absolute h-px w-full"
          style={{
            top: `${y}%`,
            background:
              "linear-gradient(90deg, transparent 0%, hsl(var(--primary) / 0.06) 50%, transparent 100%)",
          }}
          animate={{ opacity: [0, 0.6, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: y * 0.03 }}
        />
      ))}

      {[25, 50, 75].map((x) => (
        <motion.div
          key={`v-${x}`}
          className="absolute w-px h-full"
          style={{
            left: `${x}%`,
            background:
              "linear-gradient(180deg, transparent 0%, hsl(var(--primary) / 0.04) 50%, transparent 100%)",
          }}
          animate={{ opacity: [0, 0.4, 0] }}
          transition={{ duration: 5, repeat: Infinity, delay: x * 0.02 }}
        />
      ))}
    </div>
  );
}

function Waveform() {
  return (
    <motion.svg
      className="absolute bottom-[30%] left-1/2 -translate-x-1/2 w-[280px] h-10 opacity-0"
      animate={{ opacity: [0, 0.25, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      viewBox="0 0 280 40"
      fill="none"
    >
      <motion.path
        d="M0 20 L60 20 L80 5 L100 35 L120 10 L140 30 L160 20 L280 20"
        stroke="hsl(var(--primary))"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.svg>
  );
}

function LogoAssembly({ phase }: { phase: number }) {
  return (
    <motion.div className="relative flex flex-col items-center gap-3">

      {/* Outer glow ring */}
      <motion.div
        className="absolute w-40 h-40 rounded-full border border-primary/30"
        initial={{ scale: 0, opacity: 0 }}
        animate={
          phase >= 0 ? { scale: [0.5, 1.2, 1], opacity: [0, 0.6, 0.2] } : {}
        }
        transition={{ duration: 1.2, ease: "easeOut" }}
      />

      {/* Secondary ring */}
      <motion.div
        className="absolute w-56 h-56 rounded-full border border-primary/10"
        initial={{ scale: 0, opacity: 0 }}
        animate={
          phase >= 0 ? { scale: [0.3, 1.3, 1], opacity: [0, 0.3, 0.1] } : {}
        }
        transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
      />

      {/* Logo animation */}
      <motion.div
        className="relative z-10 flex items-center justify-center"
        initial={{ scale: 0, opacity: 0, rotate: -20 }}
        animate={phase >= 1 ? { scale: 1, opacity: 1, rotate: 0 } : {}}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      >
        <img
          src={logo}
          alt="ApneDoctors"
          className="w-64 object-contain select-none pointer-events-none"
        />

        {/* Glow */}
        <div
          className="absolute inset-0"
          style={{
            filter: "blur(40px)",
            background: "hsl(var(--primary) / 0.15)",
          }}
        />
      </motion.div>

    </motion.div>
  );
}

interface SplashScreenProps {
  onComplete: () => void;
}

export default function SplashScreen({ onComplete }: SplashScreenProps) {
  const [phase, setPhase] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
  const timers = [
    setTimeout(() => setPhase(1), 630),
    setTimeout(() => setPhase(2), 1580),
    setTimeout(() => setPhase(3), 3470),
    setTimeout(() => setExiting(true), 5050),
    setTimeout(() => onComplete(), 6000),
  ];

  return () => {
    timers.forEach(clearTimeout);
  };
}, [onComplete]);

  useEffect(() => {
    if (phase < 2) return;

    const interval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % AI_MESSAGES.length);
    }, 500);

    return () => clearInterval(interval);
  }, [phase]);

  return (
    <AnimatePresence>
      {!exiting ? (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background"
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <NeuralGrid />
          <ParticleField />
          <Waveform />

          <div
            className="absolute w-[500px] h-[500px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)",
            }}
          />

          <div className="relative z-10 flex flex-col items-center gap-8">
            <LogoAssembly phase={phase} />

            <motion.div
              className="h-5 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={phase >= 2 ? { opacity: 1 } : {}}
              transition={{ duration: 0.4 }}
            >
              <AnimatePresence mode="wait">
                <motion.p
                  key={msgIndex}
                  className="text-xs tracking-widest uppercase text-muted-foreground text-center"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {AI_MESSAGES[msgIndex]}
                </motion.p>
              </AnimatePresence>
            </motion.div>

            <motion.div
              className="w-48 h-0.5 rounded-full bg-muted overflow-hidden"
              initial={{ opacity: 0 }}
              animate={phase >= 1 ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
            >
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2.8, ease: "easeInOut", delay: 0.5 }}
              />
            </motion.div>

            <motion.div
              className="flex items-center gap-6"
              initial={{ opacity: 0 }}
              animate={phase >= 3 ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
            >
              {TRUST_ITEMS.map((item, i) => (
                <motion.div
                  key={item.label}
                  className="flex items-center gap-1.5"
                  initial={{ opacity: 0, y: 6 }}
                  animate={phase >= 3 ? { opacity: 0.6, y: 0 } : {}}
                  transition={{ delay: i * 0.08, duration: 0.3 }}
                >
                  <item.icon className="w-3 h-3 text-primary" />
                  <span className="text-[9px] uppercase tracking-wider text-muted-foreground hidden sm:inline">
                    {item.label}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          className="fixed inset-0 z-[100] bg-background"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      )}
    </AnimatePresence>
  );
}
